import { Component, Inject, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { Color, Icons, Modal, ModalServiceToken } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { ProspectService } from '@lsq-frontend/remote-services/ar-app';
import { DrApplicationService } from '@lsqlabs/dr-application-shared';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { first } from 'rxjs/operators';

@Component({
    selector: 'prospect-watchers-modal',
    templateUrl: './prospect-watchers-modal.component.html',
    styleUrls: ['./prospect-watchers-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class ProspectWatchersModalComponent {
    public modal: Modal;
    public watchers: Array<string>;
    public icons = Icons;
    public color = Color;
    public existingWatcherValidator = (control: AbstractControl): { [key: string]: any } | null => {
        if (!!control.value && this.watchers.includes(control.value.toLowerCase())) {
            return {
                invalidEmail: {
                    message: this._i18n('This email address is already added.')
                }
            };
        }
        return null;
    };
    public newEmailField = new FormControl('', [
        Validators.required,
        LsqValidators.email,
        this.existingWatcherValidator
    ]);
    public formGroup: FormGroup = new FormGroup({
        newEmail: this.newEmailField
    });
    public watchersChanged: boolean = false;
    public newEmail: string;
    public currentUser: string;
    public subtitlePart1: string = this._i18n({
        value: 'The following is a manageable list of the current watchers for this prospect '
    });
    public subtitlePart2: string = this._i18n({
        value: 'Watchers will receive notifications when documents are updated.'
    });
    public prospectName: string;
    public prospectId: string;
    public isSubmitting: boolean = false;

    constructor(
        @Inject(ModalServiceToken) public modalService: ModalService,
        public alertService: AlertService,
        public _arService: DrApplicationService,
        public _userService: UserService,
        public _prospectService: ProspectService,
        private _i18n: I18n
    ) {
        this.modal = this.modalService.modal;
        this.watchers = Object.assign([], this._arService.getProspect()?.watchers);
        this.prospectName = this._arService.getProspect()?.businessName;
        this.prospectId = this._arService.getProspect()?.id;
        this._userService.user$
            .pipe(first())
            .subscribe((user) => (this.currentUser = user.emailAddress));
    }

    public checkChanges(): void {
        this.watchersChanged = true;
        const oldWatchers = this._arService.getProspect().watchers;
        if (
            this.watchers.every((watcher) => oldWatchers.includes(watcher)) &&
            oldWatchers.every((watcher) => this.watchers.includes(watcher))
        ) {
            this.watchersChanged = false;
        }
    }

    public addWatcher(): void {
        this.watchers.push(this.newEmail.toLowerCase());
        this.newEmail = ''; // Reset the new email field
        this.formGroup.controls.newEmail.reset();
        this.checkChanges();
    }

    public addCurrentUser(): void {
        this.watchers.push(this.currentUser.toLowerCase());
        this.checkChanges();
    }

    public removeWatcher(watcher: string): void {
        this.watchers.splice(this.watchers.indexOf(watcher), 1);
        this.checkChanges();
    }

    public currentUserWatching(): Boolean {
        if (!this.currentUser) {
            return false;
        }
        return this.watchers.includes(this.currentUser);
    }

    public async confirmModal(): Promise<void> {
        this.isSubmitting = true;
        const updateWatchers = this._prospectService.updateWatchers(this.watchers, this.prospectId);
        const updateWatchersResponse = await updateWatchers.toPromise();
        if (updateWatchersResponse) {
            this.alertService.success('Watchers updated successfully');
            this._arService.setProspect(updateWatchersResponse);
        } else {
            this.alertService.error('Error updating watchers');
        }
        this.modalService.closeModal();
    }
}
